import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { medicationService } from '../../Services/medicationService';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Send, Bot, User as UserIcon, Loader2,
  Pill, AlertTriangle, Activity, Info, Shield, Syringe,
  Package, Heart, XCircle, CheckCircle, BookOpen, FlaskConical,
  BadgeCheck, LayoutList,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import QuotaLimitModal from '../../components/QuotaLimitModal';
import logRecentSearch from './utils/logRecentSearch';

// ─── Sections de navigation ───────────────────────────
const SECTIONS = [
  {
    id: 'indications',
    label: 'Indications',
    icon: Info,
    color: { tab: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', dot: 'bg-blue-500', icon: 'text-blue-500' },
  },
  {
    id: 'posologie',
    label: 'Posologie',
    icon: Pill,
    color: { tab: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-800', dot: 'bg-teal-500', icon: 'text-teal-500' },
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: Syringe,
    color: { tab: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-800', dot: 'bg-indigo-500', icon: 'text-indigo-500' },
  },
  {
    id: 'surveillance',
    label: 'Surveillance',
    icon: Activity,
    color: { tab: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', dot: 'bg-amber-500', icon: 'text-amber-500' },
  },
  {
    id: 'effets',
    label: 'Effets indés.',
    icon: AlertTriangle,
    color: { tab: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', dot: 'bg-orange-500', icon: 'text-orange-500' },
  },
  {
    id: 'interactions',
    label: 'Interactions',
    icon: XCircle,
    color: { tab: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', dot: 'bg-red-500', icon: 'text-red-500' },
  },
  {
    id: 'contreindications',
    label: 'Contre-indic.',
    icon: Shield,
    color: { tab: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-200 dark:border-rose-800', dot: 'bg-rose-500', icon: 'text-rose-500' },
  },
  {
    id: 'education',
    label: 'Éducation',
    icon: Heart,
    color: { tab: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500', icon: 'text-emerald-500' },
  },
  {
    id: 'autres',
    label: 'Divers',
    icon: Package,
    color: { tab: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-200 dark:border-slate-700', dot: 'bg-slate-500', icon: 'text-slate-500' },
  },
];

const PRO_SUGGESTIONS = [
  { text: 'Dilution, débit et voie d\'administration recommandés ?', icon: Syringe },
  { text: 'Paramètres cliniques à surveiller après administration ?', icon: Activity },
  { text: 'Transmissions IDE à noter au dossier ?', icon: BookOpen },
  { text: 'Compatibilités intraveineuses et incompatibilités ?', icon: FlaskConical },
];

// ─── Sous-composants ──────────────────────────────────

function InfoBlock({ label, value, subColor = 'teal' }) {
  if (!value) return null;
  return (
    <div className="space-y-1.5">
      <p className={`text-[10px] font-bold uppercase tracking-widest text-${subColor}-600 dark:text-${subColor}-400`}>
        {label}
      </p>
      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
        {value}
      </p>
    </div>
  );
}

function ArrayList({ items, colorClass }) {
  const list = Array.isArray(items) ? items : typeof items === 'string' ? [items] : [];
  if (list.length === 0)
    return <p className="text-sm text-slate-400 dark:text-slate-500 italic">Aucune donnée disponible.</p>;
  return (
    <ul className="space-y-2">
      {list.map((item, i) => (
        <li
          key={i}
          className={`flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300 p-3 rounded-xl ${colorClass.bg} border ${colorClass.border}`}
        >
          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${colorClass.dot}`} />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Divider() {
  return <hr className="border-slate-100 dark:border-slate-800" />;
}

function SectionContent({ sectionId, data }) {
  const sec = SECTIONS.find(s => s.id === sectionId);
  const cs = sec?.color;

  const effets = data.effetsIndesirables || data.effetsIndésirables;

  switch (sectionId) {
    case 'indications':
      return (
        <div className="space-y-5">
          <InfoBlock label="Indications thérapeutiques" value={data.indications} subColor="blue" />
          {data.mecanismeAction && (
            <>
              <Divider />
              <InfoBlock label="Mécanisme d'action" value={data.mecanismeAction} subColor="indigo" />
            </>
          )}
        </div>
      );

    case 'posologie':
      if (typeof data.posologie === 'string') {
        return <InfoBlock label="Posologie" value={data.posologie} subColor="teal" />;
      }
      return (
        <div className="space-y-5">
          {data.posologie?.adulte && <InfoBlock label="Adulte" value={data.posologie.adulte} subColor="teal" />}
          {data.posologie?.enfant && (
            <>
              <Divider />
              <InfoBlock label="Pédiatrie" value={data.posologie.enfant} subColor="indigo" />
            </>
          )}
          {data.posologie?.ajustements && (
            <>
              <Divider />
              <InfoBlock label="Ajustements (IR / IH / obésité)" value={data.posologie.ajustements} subColor="amber" />
            </>
          )}
        </div>
      );

    case 'administration':
      return (
        <div className="space-y-5">
          {data.administration && <InfoBlock label="Modalités d'administration (reconstitution, dilution, débit)" value={data.administration} subColor="indigo" />}
          {data.presentations && (
            <>
              <Divider />
              <InfoBlock label="Présentations & formes disponibles" value={data.presentations} subColor="slate" />
            </>
          )}
          {data.composition && (
            <>
              <Divider />
              <InfoBlock label="Composition (PA + excipients notables)" value={data.composition} subColor="slate" />
            </>
          )}
        </div>
      );

    case 'surveillance':
      return (
        <div className="space-y-5">
          <InfoBlock label="Surveillance clinique & biologique post-administration" value={data.surveillance} subColor="amber" />
        </div>
      );

    case 'effets':
      if (!effets) {
        return <p className="text-sm text-slate-400 italic">Aucune donnée disponible.</p>;
      }
      if (typeof effets === 'string' || Array.isArray(effets)) {
        return <ArrayList items={effets} colorClass={cs} />;
      }
      return (
        <div className="space-y-6">
          {effets?.frequents?.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500">Fréquents (&gt;1/10)</p>
              <ArrayList items={effets.frequents} colorClass={cs} />
            </div>
          )}
          {effets?.occasionnels?.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500">Occasionnels (1/100 – 1/10)</p>
              <ArrayList
                items={effets.occasionnels}
                colorClass={{ bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', dot: 'bg-amber-500' }}
              />
            </div>
          )}
          {effets?.graves?.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">Graves — arrêt immédiat</p>
              <ArrayList
                items={effets.graves}
                colorClass={{ bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', dot: 'bg-red-500' }}
              />
            </div>
          )}
        </div>
      );

    case 'interactions':
      return <ArrayList items={data.interactions} colorClass={cs} />;

    case 'contreindications':
      return <ArrayList items={data.contreIndications} colorClass={cs} />;

    case 'education':
      return (
        <div className="space-y-5">
          {data.educationPatient && <InfoBlock label="Éducation thérapeutique du patient" value={data.educationPatient} subColor="emerald" />}
          {data.grossesse && (
            <>
              <Divider />
              <InfoBlock
                label="Grossesse & Allaitement"
                value={typeof data.grossesse === 'string' ? data.grossesse : JSON.stringify(data.grossesse)}
                subColor="pink"
              />
            </>
          )}
          {data.conduite && (
            <>
              <Divider />
              <InfoBlock label="Impact sur la conduite" value={data.conduite} subColor="amber" />
            </>
          )}
        </div>
      );

    case 'autres':
      return (
        <div className="space-y-5">
          {data.conservation && <InfoBlock label="Conservation" value={data.conservation} subColor="slate" />}
          {data.surdosage && (
            <>
              <Divider />
              <InfoBlock label="Conduite en cas de surdosage" value={data.surdosage} subColor="red" />
            </>
          )}
          {data.references && (
            <>
              <Divider />
              <InfoBlock label="Références (HAS / ANSM / VIDAL)" value={data.references} subColor="slate" />
            </>
          )}
        </div>
      );

    default:
      return null;
  }
}

// ─── Page principale ──────────────────────────────────
export default function MedicamentProPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme === 'dark';
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [medicationData, setMedicationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('tout');
  const [showQuotaModal, setShowQuotaModal] = useState(false);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (chatMessages.length > 1) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [chatInput]);

  const performSearch = async (term) => {
    if (!term) return;
    const normalizedTerm = term.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();
    setSearchQuery(normalizedTerm);
    setLoading(true);
    setMedicationData(null);
    setChatMessages([]);
    setActiveSection('tout');

    try {
      const data = await medicationService.getMedicationInfo(normalizedTerm, 'infirmier');
      if (!data.exists) {
        toast.error('Médicament non trouvé ou non reconnu');
        return;
      }
      setMedicationData(data);
      logRecentSearch(user?.uid, { type: 'medicament', label: normalizedTerm }).catch(() => {});
      setChatMessages([
        {
          role: 'assistant',
          content: `Bonjour ! Je suis disponible pour répondre à vos questions professionnelles sur **${data.name}**${data.dci ? ` (${data.dci})` : ''}. Administration, surveillance, transmissions IDE… n'hésitez pas.`,
          timestamp: new Date(),
        },
      ]);
      toast.success(data.bdpmVerified ? 'Fiche chargée · Données BDPM officielles' : 'Fiche médicament chargée');
    } catch (error) {
      if (error.message === 'QUOTA_EXCEEDED') setShowQuotaModal(true);
      else toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const term = location.state?.searchTerm;
    if (term) performSearch(term);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    performSearch(searchQuery.trim());
  };

  const handleChatSend = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading || !medicationData) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setChatLoading(true);

    try {
      const response = await medicationService.askAboutMedication(
        medicationData.name,
        userMessage,
        chatMessages,
        'infirmier',
        medicationData.firestoreData || null
      );
      setChatMessages(prev => [...prev, { role: 'assistant', content: response, timestamp: new Date() }]);
    } catch (error) {
      setChatMessages(prev => prev.slice(0, -1));
      if (error.message === 'QUOTA_EXCEEDED') setShowQuotaModal(true);
      else toast.error("Erreur lors de l'envoi");
    } finally {
      setChatLoading(false);
    }
  };

  const handleReset = () => {
    setMedicationData(null);
    setChatMessages([]);
    setSearchQuery('');
    setActiveSection('tout');
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const formatMsg = (content) => ({
    __html: DOMPurify.sanitize(
      content
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        .replace(/\n/g, '<br/>'),
      {
        ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a'],
        ALLOWED_ATTR: ['href', 'target', 'rel']
      }
    ),
  });

  const activeSec = SECTIONS.find(s => s.id === activeSection);

  return (
    <div className={isDark ? 'dark' : ''}>
      <div className="flex flex-col h-screen bg-slate-50 dark:bg-slate-950 transition-colors">

        {/* ── Header ── */}
        <header className="shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm z-10">
          <div className="px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition text-sm shrink-0"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Retour</span>
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />

            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                <Pill size={16} className="text-teal-600 dark:text-teal-400" />
              </div>
              <div className="hidden sm:block">
                <h1 className="font-bold text-slate-800 dark:text-slate-100 text-sm leading-tight">
                  Bibliothèque Médicaments
                </h1>
                <p className="text-[10px] text-slate-400">Niveau IDE · Administration · Surveillance</p>
              </div>
            </div>

            {/* Barre de recherche */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2 items-center ml-1">
              <div className="relative flex-1 max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Médicament, DCI, molécule…"
                  className="w-full pl-8 pr-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !searchQuery.trim()}
                className="px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition flex items-center gap-1.5 shrink-0"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                <span className="hidden sm:inline">Rechercher</span>
              </button>
            </form>
          </div>
        </header>

        {/* ── État vide ── */}
        {!medicationData && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
            <div className="w-20 h-20 bg-teal-500/10 rounded-3xl flex items-center justify-center">
              <Pill size={36} className="text-teal-500" />
            </div>
            <div className="text-center max-w-md">
              <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-2">
                Bibliothèque Médicaments IDE
              </h2>
              <p className="text-sm text-slate-400 dark:text-slate-500 leading-relaxed">
                Recherchez un médicament pour accéder à la fiche posologie, les modalités
                d'administration, la surveillance clinique IDE et poser vos questions au chat IA.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-xl w-full">
              {['Amoxicilline', 'Morphine', 'Héparine', 'Métronidazole'].map(med => (
                <button
                  key={med}
                  onClick={() => setSearchQuery(med)}
                  className="px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-600 dark:hover:text-teal-400 transition"
                >
                  {med}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Chargement ── */}
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 size={40} className="animate-spin text-teal-500" />
              <p className="text-sm text-slate-400">Chargement de la fiche médicament…</p>
            </div>
          </div>
        )}

        {/* ── Contenu principal ── */}
        {medicationData && (
          <div className="flex flex-1 overflow-hidden">

            {/* ── Panneau info ── */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">

              {/* Carte d'identité du médicament */}
              <div className="shrink-0 mx-4 mt-4">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
                      <Pill size={20} className="text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-lg font-black text-slate-800 dark:text-white leading-tight">
                          {medicationData.name}
                        </h2>
                        {medicationData.dci && (
                          <span className="text-xs px-2 py-0.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-full font-semibold border border-teal-200 dark:border-teal-800">
                            DCI : {medicationData.dci}
                          </span>
                        )}
                        {medicationData.bdpmVerified ? (
                          <span className="flex items-center gap-1 text-[10px] text-teal-600 dark:text-teal-400 font-semibold">
                            <BadgeCheck size={11} />
                            BDPM officiel
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                            <CheckCircle size={11} />
                            Vérifié
                          </span>
                        )}
                      </div>
                      {medicationData.classification && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {medicationData.classification}
                        </p>
                      )}
                      {medicationData.firestoreData && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {medicationData.firestoreData.forme && (
                            <span className="text-[10px] px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-full border border-slate-200 dark:border-slate-700">
                              {medicationData.firestoreData.forme}
                            </span>
                          )}
                          {medicationData.firestoreData.voie && (
                            <span className="text-[10px] px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-full border border-slate-200 dark:border-slate-700">
                              {medicationData.firestoreData.voie}
                            </span>
                          )}
                          {medicationData.firestoreData.commercialisation && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                              medicationData.firestoreData.commercialisation.toLowerCase().includes('commercialis')
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                            }`}>
                              {medicationData.firestoreData.commercialisation}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleReset}
                      className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition"
                      title="Nouvelle recherche"
                    >
                      <XCircle size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs de sections */}
              <div className="shrink-0 px-4 pt-3">
                <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">

                  {/* Onglet Tout */}
                  <button
                    onClick={() => setActiveSection('tout')}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${
                      activeSection === 'tout'
                        ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-700 dark:border-slate-200'
                        : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <LayoutList size={13} />
                    <span>Tout</span>
                  </button>

                  {/* Onglets individuels */}
                  {SECTIONS.map(sec => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    const cs = sec.color;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => setActiveSection(sec.id)}
                        className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${
                          isActive
                            ? `${cs.bg} ${cs.tab} ${cs.border}`
                            : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <SIcon size={13} />
                        <span>{sec.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contenu de section */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">

                {/* ── Vue Tout ── */}
                {activeSection === 'tout' && (
                  <div className="space-y-3">
                    {SECTIONS.map(sec => (
                      <div
                        key={sec.id}
                        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden"
                      >
                        <div className={`flex items-center gap-2.5 px-5 py-3.5 border-b ${sec.color.border} ${sec.color.bg}`}>
                          <div className="w-7 h-7 rounded-lg bg-white/60 dark:bg-slate-900/40 flex items-center justify-center">
                            <sec.icon size={15} className={sec.color.icon} />
                          </div>
                          <h2 className={`font-bold text-sm ${sec.color.tab}`}>{sec.label}</h2>
                        </div>
                        <div className="p-5">
                          <SectionContent sectionId={sec.id} data={medicationData} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Vue section unique ── */}
                {activeSection !== 'tout' && (
                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
                    {activeSec && (
                      <div className={`flex items-center gap-2.5 px-5 py-3.5 border-b ${activeSec.color.border} ${activeSec.color.bg}`}>
                        <div className="w-7 h-7 rounded-lg bg-white/60 dark:bg-slate-900/40 flex items-center justify-center">
                          <activeSec.icon size={15} className={activeSec.color.icon} />
                        </div>
                        <h2 className={`font-bold text-sm ${activeSec.color.tab}`}>
                          {activeSec.label}
                        </h2>
                      </div>
                    )}
                    <div className="p-5">
                      <SectionContent sectionId={activeSection} data={medicationData} />
                    </div>
                  </div>
                )}

                {/* Disclaimer pro */}
                <div className="flex items-start gap-2 text-xs text-slate-400 dark:text-slate-500 pb-2">
                  <AlertTriangle size={12} className="shrink-0 mt-0.5 text-amber-400" />
                  <span>
                    Outil d'aide à la décision — à vérifier avec les protocoles de votre établissement et les prescriptions en cours.
                  </span>
                </div>
              </div>
            </div>

            {/* ── Chat IA (desktop) ── */}
            <aside className="hidden lg:flex flex-col w-96 shrink-0 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              {/* En-tête chat */}
              <div className="shrink-0 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                    <Bot size={15} className="text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Assistant IDE</p>
                    <p className="text-[10px] text-slate-400">{medicationData.name} · Niveau professionnel</p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-lg overflow-hidden shrink-0 shadow-sm border border-slate-100 dark:border-slate-700">
                        <img src="/logoR2.png" alt="IA" className="w-full h-full object-contain scale-125" />
                      </div>
                    )}
                    <div
                      className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                        msg.role === 'user'
                          ? 'bg-teal-500 text-white rounded-tr-sm'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-tl-sm'
                      }`}
                    >
                      <div
                        className={`prose prose-sm max-w-none leading-relaxed ${
                          msg.role === 'user' ? 'prose-invert' : 'dark:prose-invert'
                        }`}
                        dangerouslySetInnerHTML={formatMsg(msg.content)}
                      />
                      <p className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-teal-100 text-right' : 'text-slate-400'}`}>
                        {msg.timestamp?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-7 h-7 rounded-lg bg-slate-700 dark:bg-slate-600 flex items-center justify-center shrink-0">
                        <UserIcon size={13} className="text-white" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Suggestions IDE */}
                {chatMessages.length === 1 && !chatLoading && (
                  <div className="space-y-2 pt-1">
                    <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1">
                      Questions fréquentes IDE
                    </p>
                    {PRO_SUGGESTIONS.map((s, i) => {
                      const SIcon = s.icon;
                      return (
                        <button
                          key={i}
                          onClick={() => setChatInput(s.text)}
                          className="w-full flex items-start gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/10 text-left transition group"
                        >
                          <div className="w-6 h-6 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
                            <SIcon size={12} className="text-teal-500" />
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300 group-hover:text-teal-600 dark:group-hover:text-teal-400 leading-snug transition">
                            {s.text}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Loading */}
                {chatLoading && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                      <Bot size={13} className="text-teal-500" />
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex gap-1">
                        {[0, 150, 300].map(d => (
                          <div
                            key={d}
                            className="w-2 h-2 bg-teal-500 rounded-full animate-bounce"
                            style={{ animationDelay: `${d}ms` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Zone de saisie */}
              <div className="shrink-0 border-t border-slate-100 dark:border-slate-800 p-3">
                <form onSubmit={handleChatSend} className="flex gap-2 items-end">
                  <textarea
                    ref={textareaRef}
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleChatSend(e);
                      }
                    }}
                    placeholder={`Question sur ${medicationData.name}…`}
                    rows={1}
                    disabled={chatLoading}
                    className="flex-1 px-3 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none text-slate-900 dark:text-slate-100 placeholder-slate-400 transition"
                    style={{ maxHeight: '100px', minHeight: '42px' }}
                  />
                  <button
                    type="submit"
                    disabled={!chatInput.trim() || chatLoading}
                    className="w-10 h-10 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-xl transition flex items-center justify-center shrink-0"
                  >
                    <Send size={15} />
                  </button>
                </form>
                <p className="text-[10px] text-slate-400 mt-1.5 text-center">
                  Réponses adaptées au niveau IDE · Non sauvegardées
                </p>
              </div>
            </aside>

          </div>
        )}

        {showQuotaModal && <QuotaLimitModal onClose={() => setShowQuotaModal(false)} />}
      </div>
    </div>
  );
}
